import { calendarMonthsBetween, formatDateForAPI } from '@/features/applicant/utils/addressHelpers'
import type { CurrentEmployer, PreviousEmployer } from '@/features/applicant/types/income'

const parseLocalDate = (apiDateStr: string): Date => {
  const [yr, mo, dy] = apiDateStr.split('-').map(Number)
  return new Date(yr, mo - 1, dy)
}

/**
 * Earliest job start date across current employers.
 * Angular `getTotalMonths()`: `startdate = min(current_employment[*].job_start_date)`.
 */
export function findEarliestCurrentJobStartDate(
  currentEmployers: Array<Pick<CurrentEmployer, 'jobStartDate'>>,
): string | undefined {
  let earliest: string | undefined

  for (const employer of currentEmployers) {
    const start = employer.jobStartDate?.trim()
    if (!start) continue

    if (!earliest) {
      earliest = start
      continue
    }

    const startDate = parseLocalDate(formatDateForAPI(start))
    const earliestDate = parseLocalDate(formatDateForAPI(earliest))
    if (startDate < earliestDate) earliest = start
  }

  return earliest
}

/**
 * Job gap (calendar months) for the previous employer at position `index`.
 * Angular `getTotalMonths()` parity: index 0 → gap from this job's end to the
 * earliest current-employment start (undefined when not employed); index > 0 →
 * gap between this row and the one above it. Purely positional; 0 means no gap.
 */
export function calculatePreviousEmployerJobGap(
  employer: Pick<PreviousEmployer, 'jobStartDate' | 'jobEndDate'>,
  index: number,
  allPrevious: Array<Pick<PreviousEmployer, 'jobStartDate' | 'jobEndDate'>>,
  currentEmployers: Array<Pick<CurrentEmployer, 'jobStartDate'>>,
): number | undefined {
  if (index === 0) {
    // Gap from prev[0].end to the earliest current-employment start.
    const endStr = employer.jobEndDate?.trim()
    if (!endStr) return undefined

    const currentStart = findEarliestCurrentJobStartDate(currentEmployers)
    if (!currentStart) return undefined

    return calendarMonthsBetween(endStr, currentStart) || 0
  }

  // Gap between this row and the one above, attributed to index `i`. Order-agnostic:
  // pick the earlier end and later start by date, then calendarMonthsBetween(end, start).
  const aboveItem = allPrevious[index - 1]
  const aboveEndStr = aboveItem?.jobEndDate?.trim()
  const aboveStartStr = aboveItem?.jobStartDate?.trim()
  const thisEndStr = employer.jobEndDate?.trim()
  const thisStartStr = employer.jobStartDate?.trim()

  if (!aboveEndStr && !aboveStartStr) return undefined
  if (!thisEndStr && !thisStartStr) return undefined

  // Find the end date of whichever job ended first.
  const aboveEnd = aboveEndStr ? parseLocalDate(formatDateForAPI(aboveEndStr)) : null
  const thisEnd = thisEndStr ? parseLocalDate(formatDateForAPI(thisEndStr)) : null

  let earlierEndStr: string | undefined
  if (aboveEnd && thisEnd) {
    earlierEndStr = aboveEnd <= thisEnd ? aboveEndStr : thisEndStr
  } else {
    earlierEndStr = aboveEndStr ?? thisEndStr
  }

  // Find the start date of whichever job started later.
  const aboveStart = aboveStartStr ? parseLocalDate(formatDateForAPI(aboveStartStr)) : null
  const thisStart = thisStartStr ? parseLocalDate(formatDateForAPI(thisStartStr)) : null

  let laterStartStr: string | undefined
  if (aboveStart && thisStart) {
    laterStartStr = aboveStart >= thisStart ? aboveStartStr : thisStartStr
  } else {
    laterStartStr = aboveStartStr ?? thisStartStr
  }

  if (!earlierEndStr || !laterStartStr) return undefined

  return calendarMonthsBetween(earlierEndStr, laterStartStr) || 0
}

/**
 * Recompute job_gap for every previous employer (Angular getTotalMonths parity,
 * run after prefill/import). Pass `currentEmployers = []` for "not employed" — the
 * index-0 gap is then cleared so the gap field disappears from the UI.
 */
export function recalculatePreviousEmployerJobGaps(
  previousEmployers: PreviousEmployer[],
  currentEmployers: CurrentEmployer[],
): PreviousEmployer[] {
  const hasCurrentEmployers = currentEmployers.some(
    (emp) => emp.jobStartDate?.trim(),
  )

  return previousEmployers.map((employer, index) => {
    const jobGap = calculatePreviousEmployerJobGap(
      employer,
      index,
      previousEmployers,
      currentEmployers,
    )

    if (jobGap === undefined) {
      // index 0 with no current employers: reference point gone → clear the gap.
      // Otherwise dates aren't ready yet → keep the existing value.
      if (index === 0 && !hasCurrentEmployers) {
        return { ...employer, jobGap: undefined, jobGapExplanation: '' }
      }
      return employer
    }

    return {
      ...employer,
      jobGap,
      jobGapExplanation: jobGap < 1 ? '' : employer.jobGapExplanation,
    }
  })
}

export function previousEmployerJobGapsEqual(
  left: PreviousEmployer[],
  right: PreviousEmployer[],
): boolean {
  if (left.length !== right.length) return false
  return left.every(
    (emp, index) =>
      emp.jobGap === right[index]?.jobGap &&
      emp.jobGapExplanation === right[index]?.jobGapExplanation,
  )
}
